import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { cn } from '@/shared/lib/cn';

function ScrollArea({
  className,
  children,
  horizontal = false,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  /** Scroll sideways (a strip of chips, a wide table) instead of down. */
  horizontal?: boolean;
}) {
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn('relative', className)}
      {...props}
    >
      {/* Radix wraps the children in its own `<div style="min-width:100%;
          display:table">` so that a horizontally scrolling area can grow past
          the viewport. A table box sizes to its content, so in a vertical area
          nothing inside it ever learns how narrow the viewport is: `truncate`
          never truncates, `min-w-0` has nothing to shrink against, and a long
          value pushes the whole column out of the window (a palette name in the
          312px render inspector dragged the settings card 84px past the screen
          edge). Vertical areas force the wrapper back to a block, which takes
          the viewport's width and lets the children clip as their classes say;
          `horizontal` areas keep Radix's table, which is what they need.
          `!` is required: Radix sets display inline. */}
      <ScrollAreaPrimitive.Viewport
        data-slot="scroll-area-viewport"
        className={cn(
          'focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1',
          !horizontal && '[&>div]:block!'
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar orientation={horizontal ? 'horizontal' : 'vertical'} />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        'flex touch-none p-px transition-colors select-none',
        orientation === 'vertical' &&
          'h-full w-2.5 border-s border-s-transparent',
        orientation === 'horizontal' &&
          'h-2.5 flex-col border-t border-t-transparent',
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}

export { ScrollArea, ScrollBar };
